import React, { useState } from "react";
import {
  Form,
  Input,
  Button,
  Typography,
  Card,
  Layout,
  message,
  Alert,
  Row,
  Col,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../store/authSlice";
import { useNavigate } from "react-router-dom";
import { RootState, AppDispatch } from "../store";
import "./Login.css";

const { Title } = Typography;

const Login: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loading } = useSelector((state: RootState) => state.auth);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [form] = Form.useForm();

  // Captcha
  const generateCaptcha = () =>
    Math.random().toString(36).substring(2, 8).toUpperCase();
  const [captcha, setCaptcha] = useState(generateCaptcha);

  const refreshCaptcha = () => {
    const next = generateCaptcha();
    setCaptcha(next);
    form.setFieldsValue({ captcha: "" });
    form.setFields([{ name: "captcha", errors: [] }]);
  };

  const onFinish = async (values: { email: string; password: string; captcha: string }) => {
    if (values.captcha.trim().toUpperCase() !== captcha) {
      message.error("Captcha does not match!");
      return;
    }

    const credentials = { ...values, deviceID: "string", isFormLogin: true };

    try {
      const resultAction = await dispatch(loginUser(credentials));
      if (loginUser.fulfilled.match(resultAction)) {
        const user = resultAction.payload;
        if (user.role === "Admin" && user.status === 1) {
          message.success("Login successful!");
          navigate("/Home");
        } else {
          setErrorMsg("You do not have permission to access this application.");
        }
      } else {
        message.error(resultAction.payload || "Invalid email or password");
        setErrorMsg("Invalid email or password");
      }
    } catch {
      message.error("Something went wrong during login.");
    }
  };

  const onValuesChange = () => {
    if (errorMsg) setErrorMsg("");
  };

  return (
    <Layout className="login-layout">
      {/* Header */}
      <Layout.Header className="login-header">
        <div className="header-logo-wrapper">
          <img src="/logo-02@2x.png" alt="Logo" className="header-logo" />
        </div>
      </Layout.Header>

      {/* Main Content */}
      <Layout.Content className="login-container">
        <div className="login-box">
          {/* Overlapping Logo */}
          <div className="login-page-logo-wrapper">
            <img
              src="/Group9135.png"
              alt="Page Logo"
              className="login-page-logo"
            />
          </div>

          {/* Login Card */}
          <Card className="login-card">
            <Title level={3} className="welcome-text">Welcome To Open Road!</Title>

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onValuesChange={onValuesChange}
              validateTrigger="onChange"
              requiredMark={false}
            >
              {/* Email */}
              <Form.Item
                label="Email Address*"
                name="email"
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Enter a valid email address" },
                  { pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Email must be in proper format" },
                ]}
              >
                <Input placeholder="Enter Email Address" />
              </Form.Item>

              {/* Password */}
              <Form.Item
                label="Password*"
                name="password"
                rules={[
                  { required: true, message: "Please enter your password" },
                  { min: 6, message: "Password must be at least 6 characters" },
                ]}
              >
                <Input.Password placeholder="Enter Password" />
              </Form.Item>

              {/* Captcha */}
              <Form.Item label="Enter Captcha*">
                <Row gutter={[8, 8]} align="middle" className="captcha-row">
                  {/* Captcha Display and Refresh Button */}
                  <Col xs={24} sm={12} md={10}>
                    <div className="captcha-display-wrapper">
                      <div className="captcha-box">{captcha}</div>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={refreshCaptcha}
                        className="captcha-refresh"
                        type="default"
                      />
                    </div>
                  </Col>

                  {/* Captcha Input Field */}
                  <Col xs={24} sm={12} md={14}>
                    <Form.Item
                      name="captcha"
                      noStyle
                      rules={[
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.reject("Enter captcha!");
                            return value.trim().toUpperCase() === captcha
                              ? Promise.resolve()
                              : Promise.reject("Captcha does not match!");
                          },
                        },
                      ]}
                    >
                      <Input placeholder="Enter The Text In The Image" className="captcha-input" />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>

              {/* Submit */}
              <Form.Item>
                <Button type="primary" htmlType="submit" className="btn-login" block loading={loading}>
                  Log In
                </Button>
              </Form.Item>
            </Form>

            {/* Error Message */}
            {errorMsg && (
              <Alert message="Login Error" description={errorMsg} type="error" showIcon style={{ marginTop: 16 }} />
            )}
          </Card>
        </div>
      </Layout.Content>
    </Layout>
  );
};

export default Login;